import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import {
  useGetCurrencyConversion,
  useGetExpenseCategory,
  useGetExpenseTypeByCategory,
  useGetFeeType,
} from "../../../../../../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiLink,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../components";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../../../../../components/textFieldCurrency/TextFieldCurrency";
import {
  DATE_FORMAT,
  DEFAULT_TOOLTIPS_BILLING_AMOUNT,
  DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
  EXCHANGE_RATE_FIELD_MAX_DIGITS_ALLOWED,
  MAX_LENGTH_100,
  MAX_LENGTH_1000,
  ROLE_IDS,
  gridSize,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  convertFirstLastAndIdToOptions,
  formatDecimalWithPrecision,
  getFormattedAmount,
  getNameByIdFromList,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  AddOrEditExpenseOrFeeFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { addFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ExpenseGenericFormWrapper } from "../../FeesAndExpenseMain.style";
import { GeneralFromExpenseDetailsFields } from "./GeneralFromExpenseDetailsFields";
interface AddFeesAndExpenseGeneralFormProps {
  type: string;
  currencyList: IdNameList[] | undefined;
  transactionDateValue?: string;
  feeCurrencyValue?: string;
}

export const AddFeesAndExpenseGeneralForm = ({
  type,
  currencyList,
  transactionDateValue = "",
  feeCurrencyValue = "",
}: AddFeesAndExpenseGeneralFormProps) => {
  const { control, getValues, setValue, trigger, clearErrors, getFieldState } =
    useFormContext();

  const {
    useFetchLabel,
    commonLabels,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [isRateDisabled, setIsRateDisabled] = useState(false);
  const getLabel = useFetchLabel(
    addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const dispatch = useDispatch();
  // API calls
  const { data: getFeeTypeApiData, isLoading: isFeeTypeApiLoading } =
    useGetFeeType();

  const {
    data: getExpenseCategoryApiData,
    isPending: isExpenseCategoryApiLoading,
  } = useGetExpenseCategory();

  const {
    mutateAsync: getIncurredUsers,
    data: getIncurredUsersApiData,
    isPending: isIncurredUsersApiLoading,
  } = useGetUsersDetailByRoleMutation();
  const {
    data: currencyData,
    isLoading: isCurrencyLoading,
    refetchWithParams: getCurrencyConversion,
    isSuccess: isCurrencySuccess,
    isError: isCurrencyError,
  } = useGetCurrencyConversion();

  useEffect(() => {
    getIncurredUsers({
      roleIds: [ROLE_IDS.projectManager, ROLE_IDS.deliveryCoach],
    });
  }, []);

  const {
    data: getExpenseTypeApiData,
    isLoading: isExpenseTypeApiLoading,
    refetchWithParams: getExpenseTypeByCategoryId,
  } = useGetExpenseTypeByCategory();

  useEffect(() => {
    if (
      isFeeTypeApiLoading ||
      isExpenseCategoryApiLoading ||
      isIncurredUsersApiLoading ||
      isExpenseTypeApiLoading ||
      isCurrencyLoading
    ) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isFeeTypeApiLoading,
    isExpenseCategoryApiLoading,
    isIncurredUsersApiLoading,
    isExpenseTypeApiLoading,
    isCurrencyLoading,
  ]);

  const feeOrExpenseCurrencyId = useWatch({
    control: control,
    name: AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId,
  });

  const unitAndQuantityWatch = useWatch({
    control: control,
    disabled: type === ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE,
    name: [
      AddOrEditExpenseOrFeeFormKey.expenseUnitPrice,
      AddOrEditExpenseOrFeeFormKey.expenseQuantity,
    ],
  });

  useEffect(() => {
    const [expenseUnitPrice, expenseQuantity] = unitAndQuantityWatch;
    let calculatedAmount = 0;
    const expenseUnitPriceFieldState = getFieldState(
      AddOrEditExpenseOrFeeFormKey.expenseUnitPrice,
    );
    const expenseQuantityFieldState = getFieldState(
      AddOrEditExpenseOrFeeFormKey.expenseQuantity,
    );
    if (
      expenseUnitPriceFieldState.isDirty ||
      expenseQuantityFieldState.isDirty
    ) {
      if (expenseQuantity && expenseUnitPrice) {
        calculatedAmount = Number(expenseUnitPrice) * Number(expenseQuantity);
      }
      setValue(
        AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount,
        String(calculatedAmount),
      );
      setValue(
        AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat,
        getFormattedAmount(
          calculatedAmount,
          CURRENCY_FORMAT_MAP[feeOrExpenseCurrencyId]?.locale,
        ),
        {
          shouldDirty: true,
        },
      );
    }
  }, [unitAndQuantityWatch]);

  const billingCurrencyNameWatch = useWatch({
    control: control,
    name: AddOrEditExpenseOrFeeFormKey.billingCurrencyName,
  });

  const feeOrExpenseCurrencyNameWatch = useWatch({
    control: control,
    name: AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyName,
  });

  const exchangeRateWatch = useWatch({
    control,
    name: AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate,
  });

  const feeOrExpenseAmountWatch = useWatch({
    control,
    name: AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount,
  });

  const feeOrExpenseCurrencyWatch = useWatch({
    control,
    name: AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId,
  });
  const transactionDateWatch = useWatch({
    control,
    name: AddOrEditExpenseOrFeeFormKey.transactionDate,
  });

  const isProgramProjectFeeType = useMemo(() => {
    return type !== ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE;
  }, [type]);

  const expenseCategoryIdWatch = useWatch({
    control,
    name: AddOrEditExpenseOrFeeFormKey.expenseCategoryId,
    disabled: isProgramProjectFeeType,
  });

  useEffect(() => {
    if (expenseCategoryIdWatch) {
      getExpenseTypeByCategoryId(expenseCategoryIdWatch);
    }
  }, [expenseCategoryIdWatch]);

  const billingCurrencyFormat = useMemo(() => {
    const currencyFormat: CurrencyFormat =
      CURRENCY_FORMAT_MAP[billingCurrencyNameWatch];
    return currencyFormat?.locale;
  }, [billingCurrencyNameWatch]);

  const feeOrExpenseCurrencyFormat = useMemo(() => {
    const currencyName = getNameByIdFromList(
      currencyList,
      feeOrExpenseCurrencyWatch,
    );
    setValue(
      AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyName,
      currencyName,
    );
    const currencyFormat: CurrencyFormat = CURRENCY_FORMAT_MAP[currencyName];
    return currencyFormat?.locale;
  }, [feeOrExpenseCurrencyWatch]);

  // Dropdown value coming from BE API
  // FeeTypes List
  const feeTypeDropdownOptions = useMemo(() => {
    if (getFeeTypeApiData?.length) {
      return setApiDataToDropDownOption(getFeeTypeApiData) as OptionsDataType[];
    }
    return [];
  }, [getFeeTypeApiData]);

  // Expense Category List
  const categoryDropdownOptions = useMemo(() => {
    if (getExpenseCategoryApiData?.length) {
      return setApiDataToDropDownOption(
        getExpenseCategoryApiData,
      ) as OptionsDataType[];
    }
    return [];
  }, [getExpenseCategoryApiData]);

  // Incurred By Users List
  const incurredByUserDropdownOptions = useMemo(() => {
    if (getIncurredUsersApiData?.length) {
      return convertFirstLastAndIdToOptions(getIncurredUsersApiData);
    }
    return [];
  }, [getIncurredUsersApiData]);

  // expense types by category
  const expenseTypeDropdown = useMemo(() => {
    if (getExpenseTypeApiData?.length) {
      return setApiDataToDropDownOption(
        getExpenseTypeApiData,
      ) as OptionsDataType[];
    }
    return [];
  }, [getExpenseTypeApiData]);

  // Currency List
  const currencySelectOptions = useMemo(() => {
    if (currencyList?.length) {
      return setApiDataToDropDownOption(currencyList) as OptionsDataType[];
    }
    return [];
  }, [currencyList]);

  const isExpenseOrFeeCurrencyMismatch = useMemo(() => {
    const billingCurrencyId = getValues(
      AddOrEditExpenseOrFeeFormKey.billingCurrency,
    );
    const currencyId = feeOrExpenseCurrencyId;
    if (
      currencyId &&
      currencyId !== billingCurrencyId &&
      transactionDateWatch
    ) {
      return true;
    }
    return false;
  }, [feeOrExpenseCurrencyId, transactionDateWatch]);

  useEffect(() => {
    const isFeeCurrencyFieldDirty = getFieldState(
      AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId,
    ).isDirty;
    if (isFeeCurrencyFieldDirty && isExpenseOrFeeCurrencyMismatch) {
      resetIsRateConvertedValue();
      resetExchangeBillingValue();
    }
  }, [feeOrExpenseCurrencyId, isExpenseOrFeeCurrencyMismatch]);

  useEffect(() => {
    if (!isExpenseOrFeeCurrencyMismatch) {
      clearErrorExchangeRate();
      setValue(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate, "");
      const amount =
        getValues(AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount) ?? "0";
      setValue(AddOrEditExpenseOrFeeFormKey.billingAmount, amount);
    }
  }, [isExpenseOrFeeCurrencyMismatch]);

  const forceToValidateExchangeRateError = () => {
    trigger(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate);
  };

  const resetExchangeBillingValue = () => {
    if (isExpenseOrFeeCurrencyMismatch) {
      setValue(
        AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat,
        DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
      );
    }
  };

  const resetIsRateConvertedValue = () => {
    setValue(AddOrEditExpenseOrFeeFormKey.isRateConverted, false);
  };

  const clearErrorExchangeRate = () => {
    clearErrors(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate);
  };

  useEffect(() => {
    const isFeeAmountWithFormatFieldDirty = getFieldState(
      AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat,
    ).isDirty;
    if (isFeeAmountWithFormatFieldDirty) {
      resetIsRateConvertedValue();
      resetExchangeBillingValue();
    }
  }, [feeOrExpenseAmountWatch, feeOrExpenseCurrencyFormat]);

  useEffect(() => {
    const isExchangeRateWithFormatFieldDirty = getFieldState(
      AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate,
    ).isDirty;
    if (isExchangeRateWithFormatFieldDirty) {
      resetExchangeBillingValue();
    }
  }, [exchangeRateWatch]);

  useEffect(() => {
    if (isCurrencySuccess && isNotEmpty(currencyData)) {
      setIsRateDisabled(isNotEmpty(currencyData));
      setValue(
        AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate,
        currencyData[0]?.conversionRate,
      );
    }
  }, [isCurrencySuccess, currencyData]);

  useEffect(() => {
    if (
      isCurrencySuccess &&
      isEmpty(currencyData) &&
      feeOrExpenseCurrencyWatch !== feeCurrencyValue
    ) {
      setValue(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate, "");
    }
  }, [isCurrencySuccess, feeOrExpenseCurrencyWatch]);

  useEffect(() => {
    if (isCurrencyError) {
      setValue(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate, "");
    }
  }, [isCurrencyError]);

  useEffect(() => {
    if (transactionDateWatch && feeOrExpenseCurrencyWatch) {
      if (
        transactionDateWatch ===
          dayjs(transactionDateValue).format(userCountryDateFormat) &&
        feeOrExpenseCurrencyWatch === feeCurrencyValue
      ) {
        setIsRateDisabled(true);
        return;
      }

      setIsRateDisabled(false);
      const transactionDateFormat = dayjs(
        transactionDateWatch,
        userCountryDateFormat,
      ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN);

      getCurrencyConversion(
        transactionDateFormat,
        feeOrExpenseCurrencyWatch,
        getValues(AddOrEditExpenseOrFeeFormKey.billingCurrency),
      );
    }
  }, [
    transactionDateWatch,
    feeOrExpenseCurrencyWatch,
    isExpenseOrFeeCurrencyMismatch,
  ]);

  useEffect(() => {
    if (feeOrExpenseCurrencyFormat && !isExpenseOrFeeCurrencyMismatch) {
      setValue(
        AddOrEditExpenseOrFeeFormKey.billingAmount,
        feeOrExpenseAmountWatch,
      );
      let billingAmount = Number(feeOrExpenseAmountWatch);

      const formattedAmount =
        feeOrExpenseAmountWatch &&
        `${getFormattedAmount(billingAmount, billingCurrencyFormat)}`;
      setValue(
        AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat,
        formattedAmount,
      );
      setValue(
        AddOrEditExpenseOrFeeFormKey.billingAmount,
        String(billingAmount),
      );
    }
  }, [feeOrExpenseAmountWatch, feeOrExpenseCurrencyFormat]);

  const handleConvertRate = () => {
    setValue(AddOrEditExpenseOrFeeFormKey.isRateConverted, true);
    clearErrorExchangeRate();
    forceToValidateExchangeRateError();
    const amount = Number(
      getValues(AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount) ?? "0",
    );
    const exchangeRate = Number(
      getValues(AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate) ??
        "0",
    );
    const convertedAmount = amount ? amount * exchangeRate : 0;
    const formatExchangeRate = convertedAmount.toFixed(2);

    setValue(
      AddOrEditExpenseOrFeeFormKey.billingAmount,
      String(formatExchangeRate),
    );
    const formattedAmount = `${getFormattedAmount(
      formatExchangeRate,
      billingCurrencyFormat,
    )}`;
    setValue(
      AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat,
      formattedAmount,
    );
  };

  const exchangeRateTooltips = () => {
    return replaceHashWithValue(
      getLabel(addFeesAndExpensesSiteCoreKeys.billingDetailsTooltipMessage),
      {
        BILLINGCURRENCY: billingCurrencyNameWatch,
        FEECURRENCY: feeOrExpenseCurrencyNameWatch,
      },
    );
  };

  const billingAmountTooltips = () => {
    return replaceHashWithValue(
      getLabel(addFeesAndExpensesSiteCoreKeys.billingDetailsAmountTooltip),
      {
        BILLINGCURRENCY: billingCurrencyNameWatch,
        EXCHANGERATE: exchangeRateWatch
          ? exchangeRateWatch
          : DEFAULT_TOOLTIPS_BILLING_AMOUNT,
      },
    );
  };

  const renderCurrencyAndAmountFields = () => {
    return (
      <>
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={AddOrEditExpenseOrFeeFormKey.feeOrExpenseCurrencyId}
          >
            <MuiSelect
              isMandatory
              label={getLabel(
                isProgramProjectFeeType
                  ? addFeesAndExpensesSiteCoreKeys.feeDetailsCurrency
                  : addFeesAndExpensesSiteCoreKeys.expenseDetailsCurrency,
              )}
              options={currencySelectOptions}
              visibleDefaultSelectOption
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat}
          >
            <TextFieldCurrency
              id={AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat}
              isMandatory
              label={getLabel(
                isProgramProjectFeeType
                  ? addFeesAndExpensesSiteCoreKeys.feeDetailsAmount
                  : addFeesAndExpensesSiteCoreKeys.expenseDetailsAmount,
              )}
              disabled={!isProgramProjectFeeType}
              isCurrencyChanged={feeOrExpenseCurrencyFormat}
              formId={AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat}
              locale={feeOrExpenseCurrencyFormat}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat}
              placeholder={
                isProgramProjectFeeType
                  ? commonLabels.inputPlaceHolder
                  : DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL
              }
              getFinalValues={(_value, valueNonFormatted) => {
                setValue(
                  AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmountWithFormat,
                  _value,
                );
                setValue(
                  AddOrEditExpenseOrFeeFormKey.feeOrExpenseAmount,
                  valueNonFormatted,
                  {
                    shouldTouch: true,
                  },
                );
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </>
    );
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const formattedExchangeRate = formatDecimalWithPrecision(
        value,
        EXCHANGE_RATE_FIELD_MAX_DIGITS_ALLOWED,
      );
      if (formattedExchangeRate !== value) {
        setValue(
          AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate,
          formattedExchangeRate,
        );
      }
    }
  };

  return (
    <ExpenseGenericFormWrapper>
      {/* general field */}
      <MuiGrid container spacing={2} className="form-section">
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={AddOrEditExpenseOrFeeFormKey.feeOrExpenseName}
          >
            <MuiTextField
              id={AddOrEditExpenseOrFeeFormKey.feeOrExpenseName}
              label={getLabel(
                isProgramProjectFeeType
                  ? addFeesAndExpensesSiteCoreKeys.feeName
                  : addFeesAndExpensesSiteCoreKeys.expenseName,
              )}
              required
              isMandatory
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_100}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDateFieldFormController
            control={control}
            className="date-field"
            label={getLabel(addFeesAndExpensesSiteCoreKeys.transactionDate)}
            name={AddOrEditExpenseOrFeeFormKey.transactionDate}
            format={userCountryDateFormat}
            valueFormat={userCountryDateFormat}
            isMandatory
            value={
              getValues(AddOrEditExpenseOrFeeFormKey.transactionDate) !== ""
                ? dayjs(
                    getValues(AddOrEditExpenseOrFeeFormKey.transactionDate),
                    userCountryDateFormat,
                  )
                : null
            }
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
            id={`${AddOrEditExpenseOrFeeFormKey.transactionDate}-field`}
          />
        </MuiGrid>
        {!isProgramProjectFeeType && (
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={AddOrEditExpenseOrFeeFormKey.expenseIncurredById}
            >
              <MuiSelect
                label={getLabel(addFeesAndExpensesSiteCoreKeys.incurredBy)}
                options={incurredByUserDropdownOptions}
                visibleDefaultSelectOption
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}
      </MuiGrid>
      {/* Fees Section */}
      {isProgramProjectFeeType && (
        <MuiBox className="form-section">
          <MuiBox className="form-section-header">
            <MuiTypography variant="h2SemiBold">
              {getLabel(addFeesAndExpensesSiteCoreKeys.feeDetailsHeading)}
            </MuiTypography>
          </MuiBox>
          <MuiGrid container spacing={2}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={AddOrEditExpenseOrFeeFormKey.feeTypeId}
              >
                <MuiSelect
                  id={AddOrEditExpenseOrFeeFormKey.feeTypeId}
                  label={getLabel(
                    addFeesAndExpensesSiteCoreKeys.feeDetailsType,
                  )}
                  options={feeTypeDropdownOptions}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            {renderCurrencyAndAmountFields()}
          </MuiGrid>
        </MuiBox>
      )}
      {/* Expense Section */}
      {!isProgramProjectFeeType && (
        <GeneralFromExpenseDetailsFields
          categoryOptions={categoryDropdownOptions}
          typeOptions={expenseTypeDropdown}
        >
          {renderCurrencyAndAmountFields()}
        </GeneralFromExpenseDetailsFields>
      )}

      {/* Billing details */}
      <MuiBox className="form-section">
        <MuiBox className="form-section-header">
          <MuiTypography variant="h2SemiBold">
            {getLabel(addFeesAndExpensesSiteCoreKeys.billingDetailsHeading)}
          </MuiTypography>
        </MuiBox>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={AddOrEditExpenseOrFeeFormKey.billingCurrencyName}
            >
              <MuiTextField
                label={getLabel(
                  addFeesAndExpensesSiteCoreKeys.billingDetailsCurrency,
                )}
                disabled
                placeholder={commonLabels.inputPlaceHolder}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          {isExpenseOrFeeCurrencyMismatch && (
            <MuiGrid size={gridSize} className="exchange-rate-input">
              <FormController
                control={control}
                name={AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate}
              >
                <MuiTextField
                  id={AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate}
                  isMandatory
                  name={
                    AddOrEditExpenseOrFeeFormKey.billingCurrencyExchangeRate
                  }
                  label={getLabel(
                    addFeesAndExpensesSiteCoreKeys.billingDetailsExchangeRate,
                  )}
                  infoMessage={exchangeRateTooltips()}
                  placeholder={commonLabels.inputPlaceHolder}
                  onBlur={resetIsRateConvertedValue}
                  type="number"
                  positiveValuesOnly
                  onChange={handleOnChange}
                  disabled={isRateDisabled}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
              <MuiLink
                dataTestId="convert-link"
                className="convert-link"
                onClick={handleConvertRate}
              >
                {getLabel(addFeesAndExpensesSiteCoreKeys.billingDetailsConvert)}
              </MuiLink>
            </MuiGrid>
          )}
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat}
            >
              <MuiTextField
                id={AddOrEditExpenseOrFeeFormKey.billingAmountWithFormat}
                isMandatory
                disabled
                label={getLabel(
                  addFeesAndExpensesSiteCoreKeys.billingDetailsAmount,
                )}
                infoMessage={billingAmountTooltips()}
                placeholder={DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      {/* Comment section */}
      <MuiGrid container columnGap={3} className="form-section">
        <MuiGrid size={{ md: 8, sm: 8, xs: 12 }}>
          <FormController
            control={control}
            name={AddOrEditExpenseOrFeeFormKey.comments}
          >
            <MuiTextField
              label={commonLabels.comment}
              placeholder={commonLabels.inputPlaceHolder}
              isTextarea
              multiline
              rows={4}
              maxLength={MAX_LENGTH_1000}
              requiredARIALabel={commonARIALabel.required}
              className="fees-expense-comment-box"
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </ExpenseGenericFormWrapper>
  );
};
